import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import RentalComparablesModal from "./RentalComparablesModal";
import {
  rentEstimateYieldPlausible,
  yieldSuspectFromBackend,
} from "../lib/yieldPlausibility";

/* V4.23 — central rent-estimate pipeline (backend: market/rent_estimate.py).
 * Two render flavours over `card.rent_estimate`:
 *   <RentEstimateChips card />  — compact chips on DealCards (immo-buy)
 *   <RentEstimateBlock card />  — detail-page block with legal warning
 * The frontend NEVER computes yields — display only.
 *
 * Klagenfurt P0 2026-08 (Review Point 2 correction) — implausibility gate
 * on stored yield: any gross_yield_avg outside [0, 15] % is treated as
 * a poisoned recompute (275–814 % Klagenfurt shape) and rendered as a
 * neutral "must be recomputed" notice instead of the number. The invalid
 * value is NEVER surfaced to Claude either (advisor gate in the backend
 * strips it before it reaches the LLM). */

const YIELD_UPPER_BOUND_PCT = 15; // absolute plausibility fence
const YIELD_LOWER_BOUND_PCT = 0;

// P0 Financial-Plausibility — delegate to the shared contract mirror so
// web/mobile/backend never diverge on the bound.
const yieldIsPlausible = rentEstimateYieldPlausible;

const fmt1 = (n) =>
  n == null
    ? "—"
    : Number(n).toLocaleString("de-AT", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
const fmt0 = (n) =>
  n == null ? "—" : Math.round(Number(n)).toLocaleString("de-AT");

// Codes that are informational only — never rendered as a warning badge.
const INFO_ONLY = new Set(["de_mietpreisbremse_new_build_exempt"]);

/* Codes that report MISSING or CONTRADICTORY source data instead of an
 * established restriction. An open legal status is not a finding, so it
 * must never borrow the amber "warning" styling (MRG badge audit
 * 2026-10-06: a Gründerzeit postcode guess was rendered with the same
 * weight as a proven Vollanwendung). */
const NEUTRAL_CODES = new Set([
  "at_mrg_scope_unknown",
  "at_mrg_new_build_partial_possible",
  "building_text_conflict",
  "building_text_structured_conflict",
]);

function warningCodes(re) {
  return (re?.rent_legal_warning_codes || []).filter((c) => !INFO_ONLY.has(c));
}

const isNeutral = (codes) => codes.every((c) => NEUTRAL_CODES.has(c));

export function RentEstimateChips({ card }) {
  const { t } = useTranslation("listing");
  const re = card?.rent_estimate;
  if (!re) return null;
  const hasYield =
    re.gross_yield_avg != null &&
    yieldIsPlausible(re) &&
    !yieldSuspectFromBackend(re);
  const codes = warningCodes(re);
  if (!hasYield && codes.length === 0) return null;
  return (
    <div
      data-testid={`deal-card-rent-estimate-${card.id}`}
      className="mt-2 flex flex-wrap items-center gap-1.5"
    >
      {hasYield && (
        <span
          data-testid={`deal-card-yield-chip-${card.id}`}
          className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-200"
          title={t("rentEstimate.title")}
        >
          {t("rentEstimate.chipYield", {
            low: fmt1(re.gross_yield_low),
            high: fmt1(re.gross_yield_high),
          })}
        </span>
      )}
      {codes.length > 0 && (
        <span
          data-testid={`deal-card-legal-warning-${card.id}`}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
            isNeutral(codes)
              ? "border-zinc-700 bg-zinc-800/60 text-zinc-300"
              : "border-amber-500/40 bg-amber-500/10 text-amber-200"
          }`}
          title={codes
            .map((c) =>
              t(`rentEstimate.legal.${c}`, {
                defaultValue: t("rentEstimate.legal.generic"),
              }),
            )
            .join(" · ")}
        >
          <span aria-hidden="true">⚖</span>
          {t(`rentEstimate.chipLegal.${codes[0]}`, {
            defaultValue: t("rentEstimate.chipLegal.generic"),
          })}
        </span>
      )}
    </div>
  );
}

export function RentEstimateBlock({ card }) {
  const { t } = useTranslation("listing");
  const [rentalCompsOpen, setRentalCompsOpen] = useState(false);
  const re = card?.rent_estimate;
  if (!re) return null;
  const yieldStored = re.gross_yield_avg != null;
  const yieldPlausible = yieldIsPlausible(re);
  const suspect = yieldSuspectFromBackend(re);
  const hasYield = yieldStored && yieldPlausible && !suspect;
  const codes = warningCodes(re);
  // Klagenfurt P0 — surface the "must be recomputed" state whenever the
  // backend flagged the estimate as suspect (it already nulled the
  // number) OR a stored yield fails the client plausibility fence. Users
  // must never see 275–3.000 % on the detail page.
  const showInvalidNotice = suspect || (yieldStored && !yieldPlausible);
  if (!hasYield && !showInvalidNotice && codes.length === 0) return null;
  return (
    <div
      data-testid="listing-detail-rent-estimate"
      className="rounded-2xl border border-zinc-800 bg-zinc-950/60 px-5 py-4 space-y-2"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-300/80">
          {t("rentEstimate.title")}
        </div>
        {hasYield && (
          <span className="text-[11px] text-zinc-500">
            {t(`rentEstimate.confidence.${re.yield_confidence_class}`, {
              defaultValue: "",
            })}
          </span>
        )}
      </div>
      {hasYield ? (
        <>
          <div
            className="text-lg text-emerald-200 font-medium tabular-nums"
            data-testid="rent-estimate-yield-range"
          >
            {t("rentEstimate.yieldRange", {
              low: fmt1(re.gross_yield_low),
              high: fmt1(re.gross_yield_high),
            })}{" "}
            <span className="text-sm text-zinc-400">
              ({t("rentEstimate.yieldAvg", { avg: fmt1(re.gross_yield_avg) })})
            </span>
          </div>
          <div className="text-sm text-zinc-300" data-testid="rent-estimate-rent-range">
            {t("rentEstimate.estRent", {
              low: fmt0(re.estimated_monthly_rent_low),
              high: fmt0(re.estimated_monthly_rent_high),
            })}
          </div>
          <div className="text-xs text-zinc-500" data-testid="rent-estimate-basis">
            {re.rent_radius_m
              ? t("rentEstimate.dataBasisRadius", {
                  count: re.rent_comparable_count_primary,
                  radius: re.rent_radius_m,
                })
              : t("rentEstimate.dataBasisLocal", {
                  count: re.rent_comparable_count_primary,
                })}
          </div>
          <button
            type="button"
            data-testid="view-rental-comparables-btn"
            onClick={() => setRentalCompsOpen(true)}
            className="mt-1 inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-400/60 rounded-full px-3 py-1.5 transition-colors"
          >
            {t("rentEstimate.viewRentalComps")}
            <span aria-hidden="true">→</span>
          </button>
          <RentalComparablesModal
            ulid={card.id}
            open={rentalCompsOpen}
            onOpenChange={setRentalCompsOpen}
          />
        </>
      ) : null}
      {showInvalidNotice && (
        <div
          data-testid="rent-estimate-invalid-notice"
          className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
        >
          {t("rentEstimate.invalidYield")}
        </div>
      )}
      {codes.length > 0 && (
        <div
          data-testid="rent-estimate-legal-warning"
          className={`rounded-xl border px-3 py-2 text-xs space-y-1 ${
            isNeutral(codes)
              ? "border-zinc-700 bg-zinc-900/60 text-zinc-300"
              : "border-amber-500/40 bg-amber-500/10 text-amber-200"
          }`}
        >
          {codes.map((c) => (
            <div key={c} className="flex items-start gap-1.5">
              <span aria-hidden="true">⚖</span>
              <span>
                {t(`rentEstimate.legal.${c}`, {
                  defaultValue: t("rentEstimate.legal.generic"),
                })}
              </span>
            </div>
          ))}
          <div className={isNeutral(codes) ? "text-zinc-400" : "text-amber-300/80"}>
            {t("rentEstimate.legalCheckNote")}
          </div>
        </div>
      )}
    </div>
  );
}
